import React, { useEffect, useMemo, useRef } from 'react';
import { Alert } from '../../../../../base/components/Alert';
import { Icon } from '../../../../../base/components/Icon';
import { useAppSelector } from '../../../../../base/hooks';
import { __t } from '../../../../../base/utils/t';
import { VacancyPreview } from '../VacancyPreview';

import './styles.scss';

interface Props {
    visible: boolean;
    visibilityHandler: () => void;
}

export const PreviewModal = ({ visible, visibilityHandler }: Props) => {
    const { description, imagesToUpload, images } = useAppSelector((state) => state.vacancyForm.fields);
    const modalRef = useRef<HTMLDivElement>(null);

    const setModalHeight = () => {
        if (modalRef?.current) {
            if (window.visualViewport) {
                modalRef.current.style.height = `${window.visualViewport.height}px`;
            } else {
                modalRef.current.style.height = `${window.innerHeight}px`;
            }
        }
    };

    useEffect(() => {
        if (visible) {
            document.documentElement.setAttribute('DisableScroll', 'true');
            document.body.style.height = `${window.innerHeight}px`;
            setModalHeight();
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', setModalHeight);
            } else {
                window.addEventListener('resize', setModalHeight);
            }
        } else {
            document.documentElement.removeAttribute('DisableScroll');
            document.body.style.height = 'inherit';
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', setModalHeight);
            } else {
                window.removeEventListener('resize', setModalHeight);
            }
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', setModalHeight);
            } else {
                window.removeEventListener('resize', setModalHeight);
            }
        };
    }, [visible, modalRef, window, setModalHeight]);

    const alert = useMemo(() => {
        const emptyDescription = !description;
        const emptyImages = !(imagesToUpload.length || images.length);

        if (!emptyDescription && !emptyImages) {
            return null;
        }

        let text = '';

        if (emptyDescription && emptyImages) {
            text = __t('Добавьте описание вакансии и фото условий работы, чтобы получать больше откликов на вакансию.');
        } else if (emptyDescription) {
            text = __t('Добавьте описание вакансии, чтобы получать больше откликов на вакансию.');
        } else if (emptyImages) {
            text = __t('Добавьте фото условий работы, чтобы получать больше откликов на вакансию.');
        }

        return (
            <Alert text={text} type='warn' icon='circle-exclamation-regular' className='vacancy-preview-modal__alert' />
        );
    }, [description, imagesToUpload, images]);

    if (visible) {
        return (
            <div className='vacancy-preview-modal' ref={modalRef}>
                <div className='vacancy-preview-modal__backdrop' onClick={visibilityHandler}></div>
                <div className='vacancy-preview-modal__content'>
                    {alert}
                    <div className='vacancy-preview-modal__preview-container'>
                        <VacancyPreview />
                    </div>
                    <button className='vacancy-preview-modal__close-button' onClick={visibilityHandler}>
                        <Icon icon='xmark-regular' size='24' color='white' />
                    </button>
                </div>
            </div>
        );
    }

    return null;
};
